import { useEffect, useMemo, useState } from 'react'
import { Calendar } from 'react-big-calendar'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import { FiUser, FiTool } from 'react-icons/fi'
import api from '../../utils/api'
import useMyPermission from '../../hooks/useMyPermission'
import useFlashMessage from '../../hooks/useFlashMessage'
import { localizer, toDateTime } from '../../shared/calendar/localizer'
import Button from '../../shared/components/Button/Button'
import TechnicianAgendaModal from '../ServiceOrder/ServiceOrder/Form/TechnicianAgendaModal'
import { ReactComponent as GeneratorIcon } from '../../shared/img/icon/generator.svg'
import './MaintenanceSchedule.css'

const STATUS_LABEL = {
    aberta: { label: 'Aberta', className: 'ms-status-aberta' },
    em_andamento: { label: 'Em andamento', className: 'ms-status-em_andamento' },
    concluida: { label: 'Concluída', className: 'ms-status-concluida' },
}

const MESSAGES = {
    month: 'Mês',
    week: 'Semana',
    day: 'Dia',
    today: 'Hoje',
    previous: 'Anterior',
    next: 'Próximo',
    date: 'Data',
    time: 'Hora',
    event: 'OS',
    noEventsInRange: 'Nenhuma ordem de serviço agendada neste período',
    showMore: (total) => `+ ${total} mais`,
}

function MaintenanceSchedule() {
    const [token] = useState(localStorage.getItem('token') || '')
    const [serviceOrders, setServiceOrders] = useState([])
    const [myPermission, setMyPermission] = useState({ group: '' })
    const [load, setLoad] = useState(false)
    const [selectedOrder, setSelectedOrder] = useState(null)
    const [modalReschedule, setModalReschedule] = useState(false)

    const { checkPermission } = useMyPermission()
    const { setFlashMessage } = useFlashMessage()
    const myPermissionView = checkPermission(myPermission, 'schedule_View')
    const myPermissionEdit = checkPermission(myPermission, 'serviceOrder_Edit')

    function getAll() {
        api.get('/serviceOrder/getall', {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => {
            if (response) {
                setServiceOrders(response.data.serviceOrders || [])
                setMyPermission(response.data.permission)
                setLoad(true)
            }
        })
    }

    useEffect(() => { getAll() }, [token])

    const events = useMemo(() => {
        return serviceOrders
            .filter((o) => o.scheduledDate && o.scheduledStartTime && o.scheduledEndTime)
            .map((o) => ({
                title: `${o.code} — ${o.client?.label || o.client?.name || 'Cliente'}`,
                start: toDateTime(o.scheduledDate, o.scheduledStartTime),
                end: toDateTime(o.scheduledDate, o.scheduledEndTime),
                resource: o,
            }))
    }, [serviceOrders])

    function eventPropGetter(event) {
        const statusValue = event.resource?.status?.value
        return { className: `ms-event ms-event-${statusValue || 'aberta'}` }
    }

    function handleSelectEvent(event) {
        setSelectedOrder(event.resource)
    }

    async function handleConfirmReschedule({ scheduledDate, scheduledStartTime, scheduledEndTime }) {
        let msgType = 'success'
        const data = await api.patch(`serviceOrder/update/${selectedOrder._id}`, {
            ...selectedOrder,
            scheduledDate,
            scheduledStartTime,
            scheduledEndTime,
        }, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => response.data)
            .catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') {
            setModalReschedule(false)
            setSelectedOrder(null)
            getAll()
        }
    }

    if (!load) {
        return <div className="pg-loading">Carregando quadro de manutenção...</div>
    }

    if (!myPermissionView) {
        return (
            <div className="pg-empty">
                <p className="pg-empty-title">Sem permissão para visualizar o quadro de manutenção</p>
            </div>
        )
    }

    return (
        <section className="pg-page">
            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <p className="pg-title">Quadro de Manutenção do Mês</p>
                        <span className="pg-title-accent" />
                    </div>
                </div>
            </div>

            <div className="ms-calendar-wrap">
                <Calendar
                    localizer={localizer}
                    events={events}
                    defaultView="month"
                    views={['month', 'week', 'day']}
                    culture="pt-BR"
                    messages={MESSAGES}
                    eventPropGetter={eventPropGetter}
                    onSelectEvent={handleSelectEvent}
                    popup
                />
            </div>

            {selectedOrder && !modalReschedule && (
                <div className="ms-detail-overlay" onClick={() => setSelectedOrder(null)}>
                    <div className="ms-detail-box" onClick={(e) => e.stopPropagation()}>
                        <div className="box-x"><div onClick={() => setSelectedOrder(null)} className="shd-modal-close">X</div></div>

                        <div className="ms-detail-header">
                            <span className="ms-detail-code">{selectedOrder.code}</span>
                            {selectedOrder.status?.value && (
                                <span className={`ms-status-badge ${STATUS_LABEL[selectedOrder.status.value]?.className || ''}`}>
                                    {STATUS_LABEL[selectedOrder.status.value]?.label || selectedOrder.status.label}
                                </span>
                            )}
                        </div>

                        <p className="ms-detail-name">{selectedOrder.name}</p>

                        <div className="ms-detail-row">
                            <FiUser size={13} />
                            <span>{selectedOrder.client?.label || selectedOrder.client?.name || '—'}</span>
                        </div>

                        {selectedOrder.generator?.label && (
                            <div className="ms-detail-row">
                                <GeneratorIcon className="ms-detail-icon" />
                                <span>{selectedOrder.generator.label}</span>
                            </div>
                        )}

                        <div className="ms-detail-row">
                            <FiTool size={13} />
                            <span>{(selectedOrder.technicians || []).map((t) => t.name || t.label).join(', ') || '—'}</span>
                        </div>

                        <div className="ms-detail-schedule">
                            {selectedOrder.scheduledDate?.split('-').reverse().join('/')} das {selectedOrder.scheduledStartTime} às {selectedOrder.scheduledEndTime}
                        </div>

                        {myPermissionEdit && (
                            <div className="ms-detail-actions">
                                <Button variant="edit" onClick={() => { setModalReschedule(true) }}>
                                    Alterar horário
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {modalReschedule && selectedOrder && (
                <TechnicianAgendaModal
                    technicians={selectedOrder.technicians || []}
                    serviceOrders={serviceOrders}
                    excludeOrderId={selectedOrder._id}
                    initialDate={selectedOrder.scheduledDate}
                    onConfirm={handleConfirmReschedule}
                    onClose={() => setModalReschedule(false)}
                />
            )}
        </section>
    )
}

export default MaintenanceSchedule
